import React, { useEffect, useState } from 'react';
import styles from './styles.module.scss';
import { useDispatch } from 'react-redux';
import {
  setHeaderConfigsAction,
  setHeaderTitleAction
} from '../../../reactRedux/Actions/headerAction';
import { generatePath, useHistory } from 'react-router-dom';
import Card from '../../common/cards/Card';
import { routes } from '../../../assets/routes';
import Loader from '../../common/Loader';
import { Helmet } from 'react-helmet';
import Eof from '../../common/Eof';
import useDesktopDisplay from '../../../hooks/useDesktopDisplay';
import { useInfiniteQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { announcementsListRequest } from '../../../helpers/api';
import Button from '@mui/material/Button';
import TabPanel from '../../common/tabs/SimpleTabPanel';
import StyledTab from '../../common/tabs/SimpleTab';
import SimpleTabs from '../../common/tabs/SimpleTabs';
import { a11yProps } from '../../../helpers/utils';
import classNames from 'classnames';
import SwipeableViews from 'react-swipeable-views';
import { useTranslation } from '../../../i18n/LanguageProvider';

const AnnouncementsList = () => {
  const { t, isRtl } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const isDesktop = useDesktopDisplay();
  const [tabIndex, setTabIndex] = useState(0);

  const handleChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  const handleChangeIndex = (index) => {
    setTabIndex(index);
  };
  const { isLoading, data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ['announcementsList'],
      ({ pageParam = 1 }) =>
        announcementsListRequest({ pageParam, myDepartment: false }),
      {
        select: React.useCallback(
          (data) => data?.pages.flatMap((announcement) => announcement.data),
          []
        ),
        getNextPageParam: (lastPage, allPages) => {
          const nextPage = allPages.length + 1;
          return lastPage.data.length !== 0 ? nextPage : undefined;
        },
        onError(err) {
          toast.error(err.response?.data?.detail);
        }
      }
    );

  const {
    isLoading: myDepartmentAnnoucementIsLoading,
    data: myDepartmentAnnoucementData,
    hasNextPage: myDepartmentAnnoucementHasNextPage,
    fetchNextPage: myDepartmentAnnoucementFetchNextPage,
    isFetchingNextPage: myDepartmentAnnoucementIsFetchingNextPage
  } = useInfiniteQuery(
    ['announcementsList', 'myDepartment'],
    ({ pageParam = 1 }) =>
      announcementsListRequest({ pageParam, myDepartment: true }),
    {
      select: React.useCallback(
        (data) => data?.pages.flatMap((announcement) => announcement.data),
        []
      ),
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.data.length !== 0 ? nextPage : undefined;
      },
      onError(err) {
        toast.error(err.response?.data?.detail);
      }
    }
  );

  const setHeader = (title) => dispatch(setHeaderTitleAction(title));
  const setHeaderConfigs = (configs) =>
    dispatch(setHeaderConfigsAction(configs));

  useEffect(() => {
    setHeader(t('announcements.title'));
    setHeaderConfigs({ credits: false, exit: false, back: true, logo: false });
  }, []);

  const openAnnouncement = (id) => () => {
    history.push(generatePath(routes.ANNOUNCEMENT, { id }));
  };

  return (
    <div className={styles.page}>
      <Helmet>
        <title>{t('announcements.title')}</title>
      </Helmet>
      {isDesktop && (
        <div className={styles.pageHeader}>{t('announcements.title')}</div>
      )}
      <div className={styles.section}>
        <div className={styles.sectionTop}>
          <SimpleTabs value={tabIndex} handleChange={handleChange}>
            <StyledTab label={t('common.all')} {...a11yProps(0)} />
            <StyledTab label={t('common.myDepartment')} {...a11yProps(1)} />
          </SimpleTabs>
        </div>
      </div>

      <SwipeableViews
        axis={isRtl ? 'x-reverse' : 'x'}
        index={tabIndex}
        onChangeIndex={handleChangeIndex}
        className={styles.swipeArea}
      >
        <TabPanel index={0} value={tabIndex}>
          {isLoading ? (
            <Loader size={40} />
          ) : data.length ? (
            data.map((ann, i) => (
              <Card
                key={i}
                extraClass={styles.card}
                onClick={openAnnouncement(ann.id)}
                image={ann.cover?.absolute_url}
              >
                {ann.title}
              </Card>
            ))
          ) : (
            <div className={classNames(styles.emptyCard, styles.kudos)}>
              {t('announcements.empty')}
            </div>
          )}
          {!isLoading &&
            (isFetchingNextPage ? (
              <Loader size={40} />
            ) : hasNextPage ? (
              <Button fullWidth variant="outlined" onClick={fetchNextPage}>
                {t('common.more')}
              </Button>
            ) : (
              <Eof />
            ))}
        </TabPanel>
        <TabPanel index={1} value={tabIndex}>
          {myDepartmentAnnoucementIsLoading ? (
            <Loader size={40} />
          ) : myDepartmentAnnoucementData.length ? (
            myDepartmentAnnoucementData.map((ann, i) => (
              <Card
                key={i}
                extraClass={styles.card}
                onClick={openAnnouncement(ann.id)}
                image={ann.cover?.absolute_url}
              >
                {ann.title}
              </Card>
            ))
          ) : (
            <div className={classNames(styles.emptyCard, styles.kudos)}>
              {t('announcements.empty')}
            </div>
          )}
          {!myDepartmentAnnoucementIsLoading &&
            (myDepartmentAnnoucementIsFetchingNextPage ? (
              <Loader size={40} />
            ) : myDepartmentAnnoucementHasNextPage ? (
              <Button
                fullWidth
                variant="outlined"
                onClick={myDepartmentAnnoucementFetchNextPage}
              >
                {t('common.more')}
              </Button>
            ) : (
              <Eof />
            ))}
        </TabPanel>
      </SwipeableViews>
    </div>
  );
};

export default AnnouncementsList;
